/* oxlint-disable no-console */
import { AnalyticsBrowser, ID } from '@segment/analytics-next';

import { convertDate } from '../datetime';
import { getServerEnv, isLocal, isServer } from '../env-helper';
import { SegmentDataType, SegmentTypes } from './events';

export * from './events';

export const convertDateFormat = (date: Date | string) => {
  return convertDate(date)!.toISOString();
};

class Segment {
  #analytics: AnalyticsBrowser | undefined = undefined;
  #debug: boolean = !isServer && ['dev', 'sandbox'].includes(getServerEnv());
  #shouldNotSendServer: boolean = !isServer && isLocal();

  #uid: string | undefined = undefined;

  load(writeKey: string) {
    if (this.#analytics) return;
    if (this.#debug) {
      console.log('[Segment - load]', { writeKey });
      if (this.#shouldNotSendServer) return;
    }
    this.#analytics = AnalyticsBrowser.load({ writeKey });
  }

  async getAnonymousId(): Promise<ID> {
    if (!this.#analytics) return undefined;
    if (this.#debug) {
      console.log('[Segment - getAnonymousId]');
      if (this.#shouldNotSendServer) return undefined;
    }
    const user = await this.#analytics.user();
    return user.anonymousId();
  }

  page(name: string, properties?: Record<string, unknown>) {
    if (!this.#analytics) return;
    if (this.#debug) {
      console.log('[Segment - page]', { name, properties });
      if (this.#shouldNotSendServer) return;
    }
    // 실제로 날아가는건 category 가 아니라 name 값으로 날아감
    this.#analytics.page(name, properties);
  }

  track<E extends SegmentTypes>(event: E, dataWithoutUid?: Omit<SegmentDataType[E], 'uid'>) {
    if (!this.#analytics) return;
    const data = { ...dataWithoutUid, uid: this.#uid };
    if (this.#debug) {
      console.log('[Segment - track]', { event, data });
      if (this.#shouldNotSendServer) return;
    }

    this.#analytics.track(event, data);
  }

  /**
   * https://community.amplitude.com/data-instrumentation-57/invalid-id-length-for-user-id-or-device-id-763
   * 5자리 미만의 device_id 및 user_id를 보내면 amplitude에서 에러가 발생한다.
   * */
  identify(id: string | undefined, uid: string | undefined) {
    if (!this.#analytics || !id) return;
    this.#uid = uid;
    const passingId = id.padStart(5, '0');
    if (this.#debug) {
      console.log('[Segment - identify]', { passingId });
      if (this.#shouldNotSendServer) return;
    }
    this.#analytics.user().then((res) => {
      if (!res.id()) {
        this.#analytics?.identify(passingId, {
          uid,
        });
      }
    });
  }

  reset() {
    if (!this.#analytics) return;
    if (this.#debug) {
      console.log('[Segment - reset]');
      if (this.#shouldNotSendServer) return;
    }
    this.#analytics.reset();
  }
}

export default new Segment();
